import React, { useState } from "react";
import { utils, writeFile } from "xlsx";

function OverallReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [overallData, setOverallData] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // State for search input

  const fetchOverallReport = () => {
    setError(null);
    setSearchQuery(""); // Reset search when fetching new data
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }

    const payload = { start_date: startDate, end_date: endDate };

    fetch("https://stock.krishtec.co.in/stock/get_overall_report.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Overall Report Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setOverallData(Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch overall report error:", err);
        setOverallData([]);
        setError("Failed to load overall report: " + err.message);
      });
  };

  const downloadExcel = () => {
    if (overallData.length === 0) {
      setError("No data to download.");
      return;
    }

    const filteredData = overallData.filter((item) =>
      item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (filteredData.length === 0) {
      setError("No matching data to download.");
      return;
    }

    const worksheetData = filteredData.map((item) => ({
      "Product ID": item.product_id,
      "Product Name": item.product_name,
      "Month": item.month,
      "Stock at Start of Month": item.stock_before,
      "Quantity Purchased": item.total_purchased,
      "Quantity Sold": item.total_sold,
      "Stock at End of Month": item.stock_after,
      "Current Stock in Inventory": item.current_in_stock,
    }));

    const worksheet = utils.json_to_sheet(worksheetData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Overall Stock Report");
    worksheet["!cols"] = [
      { wch: 12 }, // Product ID
      { wch: 20 }, // Product Name
      { wch: 10 }, // Month
      { wch: 20 }, // Stock at Start
      { wch: 15 }, // Quantity Purchased
      { wch: 15 }, // Quantity Sold
      { wch: 20 }, // Stock at End
      { wch: 20 }, // Current Stock
    ];
    writeFile(workbook, `Overall_Stock_Report_${startDate}_to_${endDate}.xlsx`);
  };

  // Filter data based on search query
  const filteredOverallData = overallData.filter((item) =>
    item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center p-6 bg-gray-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">Overall Stock Report</h1>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Date Inputs and Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-end bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex gap-4">
            <button
              onClick={fetchOverallReport}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Fetch Report
            </button>
            <button
              onClick={downloadExcel}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {overallData.length > 0 && (
          <div className="mb-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">Search Products</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter product name..."
              className="w-full sm:w-1/3 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Report Table */}
        {overallData.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Product ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Stock at Start
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Qty Purchased
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Qty Sold
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Stock at End
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Current Stock
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredOverallData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.product_id || "N/A"}</td>
                      <td className="px-6 py-4 text-gray-700">{item.product_name || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.month || "N/A"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.stock_before ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.total_purchased ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.total_sold ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.stock_after ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">{item.current_in_stock ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredOverallData.length === 0 && searchQuery && (
              <div className="text-center text-gray-500 py-6">
                No products found matching "{searchQuery}".
              </div>
            )}
          </div>
        )}

        {overallData.length === 0 && !error && startDate && endDate && (
          <div className="text-center text-gray-500 mt-6">No data found for the selected date range.</div>
        )}
      </div>
    </div>
  );
}

export default OverallReport;